'use client'
import { FC, useState } from 'react'
import { Popover } from 'antd'
import useI18n from 'Hooks/useI18n'
import { useRecoilState } from 'recoil'
import { userState } from 'Store/user'
import { setUserLanguage, getUser } from '@/apis/user'
import { storageKey } from '@/constant/storage'
import { GlobeIcon } from '@/components/svg'
import { Language, SUPPORTER_LANGUAGES } from '@/constant/lang'
import { withLocale } from '@/locales'
import CustomScrollView from '@/components-mobile/ui-kit/CustomScrollView'

const content: FC<{
  languages: Language[]
  selectedLanguage: string
  handleLanguageChange: (lang: string) => Promise<void>
}> = ({ languages, selectedLanguage, handleLanguageChange }) => (
  <CustomScrollView
    style={{
      height: 'min(70vh, 560px)',
      minWidth: '130px',
    }}
    contentContainerStyle={{
      paddingTop: '8px',
      paddingBottom: '8px',
    }}
    scrollbarTrackStyle={{
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    }}
    scrollbarThumbStyle={{
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
    }}
  >
    {languages.map((language) => (
      <div
        key={language.language_code}
        className={`flex cursor-pointer items-center px-4 py-2.5 text-sm hover:bg-white/5 ${
          selectedLanguage === language.language_code
            ? 'bg-white/5 text-[#FF6480]'
            : 'text-white'
        }`}
        onClick={() => handleLanguageChange(language.language_code)}
      >
        {language.display}
        {selectedLanguage === language.language_code && (
          <svg
            className="ml-auto h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>
    ))}
  </CustomScrollView>
)

export default function LanguageSelect() {
  const { t, lang } = useI18n()
  const [user, setUser] = useRecoilState(userState)
  const [open, setOpen] = useState(false)

  const handleLanguageChange = async (targetLang: string) => {
    console.log('🌐 User selected language:', targetLang)
    if (targetLang === lang) {
      setOpen(false)
      return
    }
    // 先关闭 Popover
    setOpen(false)

    // 异步执行语言切换逻辑
    try {
      if (user) {
        await setUserLanguage(targetLang)
        const userData = await getUser()
        if (userData) {
          setUser(userData)
          const userJson = localStorage.getItem(storageKey.USER)
          if (userJson) {
            const localUser = JSON.parse(userJson)
            localUser.language_code = targetLang
            localStorage.setItem(storageKey.USER, JSON.stringify(localUser))
          }
        }
      } else {
        // 未登录用户：将语言设置保存到 localStorage
        localStorage.setItem(
          storageKey.UNLOGGED_USER_SELECTED_LANGUAGE,
          targetLang
        )
      }

      const pathnameWithLocale = withLocale(
        window.location.pathname,
        targetLang
      )
      if (window.location.pathname !== pathnameWithLocale) {
        window.location.href = pathnameWithLocale + window.location.search
      }
    } catch (error) {
      console.error('Failed to set language:', error)
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      content={content({
        languages: SUPPORTER_LANGUAGES,
        selectedLanguage: lang,
        handleLanguageChange,
      })}
      trigger="hover"
      overlayInnerStyle={{
        background:
          'linear-gradient(180deg, rgba(10,16,24,0.78) 0%, rgba(14,22,32,0.74) 100%)',
        border: '1px solid rgba(255,255,255,0.38)',
        boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
        padding: '0',
        borderRadius: '10px',
      }}
      align={{
        offset: [0, 12],
      }}
      placement="bottom"
      arrow={false}
    >
      <button
        aria-label={t('Language')}
        className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full border border-secondary-border bg-secondary-bg transition hover:border-hover-border"
      >
        <GlobeIcon className="h-6 w-6" />
      </button>
    </Popover>
  )
}
